import type { TemplateId } from '@/types/portfolio';
import { cn } from '@/lib/utils/cn';

interface TemplateThumbProps {
  id: TemplateId;
  className?: string;
}

export function TemplateThumb({ id, className }: TemplateThumbProps) {
  const styles: Record<TemplateId, { wrap: string; content: React.ReactNode }> = {
    minimal: {
      wrap: 'bg-canvas',
      content: (
        <div className="flex h-full flex-col justify-between p-5">
          <div className="flex items-center justify-between font-mono text-[8px] uppercase tracking-[0.22em] text-ink-faint">
            <span>Portfolio · 01</span>
            <span>2026</span>
          </div>
          <div>
            <p className="font-display text-[18px] font-semibold leading-[1.1] text-ink">
              A structural field guide to a year of making.
            </p>
            <p className="mt-2 text-[9px] text-ink-muted">Independent systems designer</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <span className="h-10 rounded-sm bg-ink/5" />
            <span className="h-10 rounded-sm bg-ink/5" />
          </div>
        </div>
      ),
    },
    'dev-log': {
      wrap: 'bg-[#F8F9FA]',
      content: (
        <div className="flex h-full flex-col gap-1.5 p-5 font-mono text-[9px] leading-[1.4] text-ink">
          <p className="text-ink-faint">~/portfolio $ ls -la</p>
          <p>drwxr-xr-x 01-cache-layer.md</p>
          <p>drwxr-xr-x 02-edge-router.md</p>
          <p>drwxr-xr-x 03-token-store.md</p>
          <p>drwxr-xr-x 04-sard-ml.md</p>
          <p className="mt-2 text-ink-faint">~/portfolio $ cat about</p>
          <p>Engineer, occasionally a builder.</p>
          <p className="text-ink-faint">~/portfolio $ _</p>
        </div>
      ),
    },
    compass: {
      wrap: 'bg-canvas',
      content: (
        <div className="grid h-full grid-cols-[62px_1fr] gap-3 p-3 text-[8px]">
          <div className="flex flex-col gap-1 rounded-sm bg-ink/[0.03] p-2">
            <p className="mb-1 font-semibold text-ink">Sections</p>
            <span className="rounded-sm bg-ink/10 px-1.5 py-0.5 text-ink">Intro</span>
            <span className="px-1.5 py-0.5 text-ink-muted">About</span>
            <span className="px-1.5 py-0.5 text-ink-muted">Projects</span>
            <span className="px-1.5 py-0.5 text-ink-muted">Skills</span>
            <span className="px-1.5 py-0.5 text-ink-muted">Contact</span>
          </div>
          <div className="flex flex-col justify-between py-1">
            <div>
              <p className="font-display text-[14px] font-bold leading-tight text-ink">
                A builder by trade.
              </p>
              <p className="mt-1 text-[9px] text-ink-muted">
                Shipping calm, navigable software for curious teams.
              </p>
              <span className="mt-2 inline-flex rounded bg-violet-600 px-1.5 py-0.5 text-[8px] font-semibold text-white">
                Get Resume ↗
              </span>
            </div>
            <div className="flex items-center justify-between text-[8px] text-ink-muted">
              <span>← About</span>
              <span className="font-medium text-ink">Projects →</span>
            </div>
          </div>
        </div>
      ),
    },
    batman: {
      wrap: 'bg-[#09090B]',
      content: (
        <div
          className="relative flex h-full flex-col justify-between p-5"
          style={{
            background:
              'radial-gradient(ellipse 60% 70% at 50% 0%, rgba(245,197,24,0.18) 0%, transparent 70%)',
          }}
        >
          <div className="flex items-center justify-between font-mono text-[8px] uppercase tracking-[0.22em] text-[#F5C518]/70">
            <span>Dossier · 01</span>
            <span>Gotham</span>
          </div>
          <div>
            <p className="font-display text-[17px] font-bold leading-[1.05] text-white">
              I am the night.
              <br />I build in it.
            </p>
            <p className="mt-2 text-[9px] text-[#9CA3AF]">Shadow architect</p>
          </div>
          <div className="flex items-center gap-2">
            <svg viewBox="0 0 40 20" className="h-4 w-8 fill-[#F5C518]">
              <path d="M20 2 L24 8 L34 4 L30 12 L38 16 L26 16 L20 20 L14 16 L2 16 L10 12 L6 4 L16 8 Z" />
            </svg>
            <span className="h-1 flex-1 bg-[#F5C518]/30" />
            <span className="font-mono text-[8px] uppercase tracking-[0.18em] text-[#F5C518]">
              Case files
            </span>
          </div>
        </div>
      ),
    },
    spiderman: {
      wrap: 'bg-[#F7F7FB]',
      content: (
        <div className="flex h-full flex-col gap-2 p-3">
          <div
            className="relative flex flex-1 flex-col justify-between rounded-md border-2 border-[#0C2340] p-3"
            style={{
              background: '#E4000F',
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><g fill='none' stroke='white' stroke-opacity='0.28' stroke-width='0.6'><circle cx='50' cy='50' r='15'/><circle cx='50' cy='50' r='28'/><circle cx='50' cy='50' r='42'/><line x1='50' y1='50' x2='50' y2='0'/><line x1='50' y1='50' x2='100' y2='0'/><line x1='50' y1='50' x2='100' y2='50'/><line x1='50' y1='50' x2='100' y2='100'/><line x1='50' y1='50' x2='50' y2='100'/><line x1='50' y1='50' x2='0' y2='100'/><line x1='50' y1='50' x2='0' y2='50'/><line x1='50' y1='50' x2='0' y2='0'/></g></svg>\")",
              backgroundSize: 'cover',
            }}
          >
            <p className="font-mono text-[7px] uppercase tracking-[0.22em] text-white/80">
              ⬢ Friendly neighborhood
            </p>
            <p className="font-display text-[14px] font-bold uppercase leading-[0.95] text-white">
              With great work
              <br />
              comes great output.
            </p>
          </div>
          <div className="flex items-center justify-between rounded-sm border-2 border-[#0C2340] bg-white px-2 py-1">
            <span className="font-mono text-[7px] uppercase tracking-[0.18em] text-[#0C2340]">
              Origin story
            </span>
            <span className="h-2 w-2 rounded-full bg-[#E4000F]" />
          </div>
        </div>
      ),
    },
  };
  const style = styles[id];
  return <div className={cn('h-full w-full', style.wrap, className)}>{style.content}</div>;
}
